import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  createAvatarProps,
  Typography,
  YStack,
} from '@usepepper/sprout-kit-ui';
import {
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
  SortingState,
  TableState,
} from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { CustomColumnDef } from '@experiences/components/table/types';
import {
  DEFAULT_COLUMN_WIDTH_PERCENTAGE,
  DEFAULT_SORTING,
  ESTIMATED_HEADER_HEIGHT,
  MIN_COLUMN_SIZE,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
  ROW_HEIGHT,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-rep-view-table/prospects-rep-view-table-columns';
import { LayoutChangeEvent, View } from 'react-native';
import { passThroughFilter } from '@/experiences/sales-portal/blocs/utils/fuzzy-search';
import { ProspectsRepViewTableRow } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-rep-view-table/prospects-rep-view-table-row';
import {
  RepViewColumnId,
  RepViewProspectRowData,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';

const columnHelper = createColumnHelper<RepViewProspectRowData>();

interface ProspectsRepViewTableProps {
  appliedSearchText: string;
  data: RepViewProspectRowData[];
  emptyComponent: React.ReactNode;
  onRowClick: (row: RepViewProspectRowData) => void;
}

export const ProspectsRepViewTable: React.FC<ProspectsRepViewTableProps> = ({
  appliedSearchText,
  data,
  emptyComponent,
  onRowClick,
}) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  const tableState = useMemo(
    (): Partial<TableState> => ({ globalFilter: appliedSearchText, sorting }),
    [appliedSearchText, sorting],
  );

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.name, {
        cell: (info) => {
          const row = info.row.original;
          const avatarProps = createAvatarProps(
            row.name,
            row.restaurantUUID,
            1,
          );

          return (
            <View style={styles.nameCellContainer}>
              <Avatar {...avatarProps} size="$sm" />
              <YStack flex={1} gap="$2">
                <Typography
                  fontWeight="500"
                  numberOfLines={1}
                  size="$sm"
                  textAlign="left"
                >
                  {row.name}
                </Typography>
                <Typography
                  color={theme.sk.color.text.tertiary}
                  numberOfLines={1}
                  size="$xs"
                  textAlign="left"
                >
                  {row.address}
                </Typography>
              </YStack>
            </View>
          );
        },
        header: () => (
          <Typography
            color={theme.sk.color.text.secondary}
            size="$xs"
            variant="label"
          >
            {i18n.t('sales_portal.prospects.table.name')}
          </Typography>
        ),
        id: RepViewColumnId.Name,
        meta: { initialPercentWidth: NAME_COLUMN_PERCENTAGE },
        minSize: MIN_NAME_COLUMN_SIZE,
      }),
      columnHelper.accessor((row) => row.lastActivityAt, {
        cell: (info) => (
          <Typography
            numberOfLines={1}
            size="$sm"
            textAlign="left"
            variant="body"
          >
            {info.row.original.lastActivityDisplay}
          </Typography>
        ),
        header: () => (
          <Typography
            color={theme.sk.color.text.secondary}
            size="$xs"
            variant="label"
          >
            {i18n.t('sales_portal.prospects.table.last_activity')}
          </Typography>
        ),
        id: RepViewColumnId.LastActivity,
        meta: { initialPercentWidth: DEFAULT_COLUMN_WIDTH_PERCENTAGE },
        minSize: MIN_COLUMN_SIZE,
        sortDescFirst: false,
        sortingFn: 'text',
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.weeklyValueInMicros, {
        cell: (info) => (
          <Typography
            numberOfLines={1}
            size="$sm"
            textAlign="left"
            variant="body"
          >
            {info.row.original.weeklyValueDisplay}
          </Typography>
        ),
        header: () => (
          <Typography
            color={theme.sk.color.text.secondary}
            size="$xs"
            variant="label"
          >
            {i18n.t('sales_portal.prospects.table.value')}
          </Typography>
        ),
        id: RepViewColumnId.Value,
        meta: { initialPercentWidth: DEFAULT_COLUMN_WIDTH_PERCENTAGE },
        minSize: MIN_COLUMN_SIZE,
        sortDescFirst: false,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.dateAddedAt, {
        cell: (info) => (
          <Typography
            numberOfLines={1}
            size="$sm"
            textAlign="left"
            variant="body"
          >
            {info.row.original.dateAddedDisplay}
          </Typography>
        ),
        header: () => (
          <Typography
            color={theme.sk.color.text.secondary}
            size="$xs"
            variant="label"
          >
            {i18n.t('sales_portal.prospects.table.date_added')}
          </Typography>
        ),
        id: RepViewColumnId.DateAdded,
        meta: { initialPercentWidth: DEFAULT_COLUMN_WIDTH_PERCENTAGE },
        minSize: MIN_COLUMN_SIZE,
        sortDescFirst: false,
        sortingFn: 'text',
      }),
    ],
    [styles, theme],
  );

  const renderHeaderRow = ({
    headerGroup,
  }: {
    headerGroup: HeaderGroup<RepViewProspectRowData>;
  }) => (
    <TableHeaderRow key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          columnId={header.id}
          key={header.id}
          onPress={
            header.column.getCanSort()
              ? () => header.column.toggleSorting()
              : undefined
          }
          sortDirection={header.column.getIsSorted() || undefined}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </TableHeaderCell>
      ))}
    </TableHeaderRow>
  );

  const renderRow = useCallback(
    ({ index, row }: { index: number; row: Row<RepViewProspectRowData> }) => (
      <ProspectsRepViewTableRow
        index={index}
        key={row.id}
        onRowClick={onRowClick}
        row={row}
      />
    ),
    [onRowClick],
  );

  if (!layoutWidth) {
    return (
      <View
        accessibilityLabel={i18n.t('sales_portal.prospects.loading')}
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true }}
        accessible
        onLayout={handleLayout}
        style={styles.tableWrapper}
      />
    );
  }

  return (
    <View onLayout={handleLayout} style={styles.tableWrapper}>
      <Table
        cellPadding={theme.sk.spacing.spaceSmall}
        columns={columns as CustomColumnDef<RepViewProspectRowData>[]}
        data={data}
        emptyComponent={emptyComponent}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        estimatedRowHeight={ROW_HEIGHT}
        globalFilterFn={passThroughFilter}
        gutters={0}
        headerPadding={theme.sk.spacing.spaceSmall}
        isActive
        layoutWidth={layoutWidth}
        onSortingChange={setSorting}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        state={tableState}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  nameCellContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    justifyContent: 'flex-start',
  },
  tableWrapper: {
    flex: 1,
  },
}));
